import { useState } from 'react';
import DataTable from 'react-data-table-component';
import toast from 'react-hot-toast';
import { FaCheck, FaEye } from 'react-icons/fa';
import { ImCancelCircle } from 'react-icons/im';
import { MdDelete } from 'react-icons/md';
import { RiProgress5Line } from 'react-icons/ri';
import ConfirmationPopup from './ConfirmationPopup';
import { isTrainingInProgressStatus, isTrainingSucceededStatus } from '../../utils/utils';

const getForecastJobName = model => model?.FORECAST_JOB_NAME || model?.MODEL_NAME || model?.MODEL_ID;

const parseCsvLikeField = (value, separator = ', ') => {
    if (!value) {
        return '';
    }
    return value
        .replace(/[[\]'"]/g, '')
        .split(', ')
        .map(tag => tag.trim())
        .filter(Boolean)
        .join(separator);
};

const getProjectInfo = data => {
    if (Array.isArray(data.PROJECT_INFO)) {
        return data.PROJECT_INFO;
    }
    return [];
};

const getBusinessRulesTable = data => {
    if (!data.BUSINESS_RULES || data.BUSINESS_RULES === '[]') {
        return <span>-</span>;
    }

    const rules = JSON.parse(data.BUSINESS_RULES.replace(/'/g, '"'));
    return (
        <table className='mt-2 bg-white border border-gray-300 rounded'>
            <thead>
                <tr className='bg-gray-100 font-normal'>
                    <th className='px-3 py-2 border-b text-left'>Alias</th>
                    <th className='px-3 py-2 border-b text-left'>Operator</th>
                    <th className='px-3 py-2 border-b text-left'>Value</th>
                </tr>
            </thead>
            <tbody>
                {rules.map((rule, idx) => (
                    <tr key={idx} className='border-b'>
                        <td className='px-3 py-2'>{rule.ALIAS}</td>
                        <td className='px-3 py-2'>{rule.OPERATOR}</td>
                        <td className='px-3 py-2'>{rule.VALUE}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const ProjectInfoSection = ({ projectInfo }) => {
    if (projectInfo.length === 0) {
        return null;
    }

    return (
        <>
            <p className='mb-1 text-base font-semibold'>Project Info</p>
            <ul>
                {projectInfo.map((info, index) => (
                    <li key={index}>
                        <p className='mb-1'>
                            <strong>Make Manufacturer:</strong> {info.MAKE_MANUFACTURER}
                            <br />
                        </p>
                        <p className='mb-1'>
                            <strong>Seal Type:</strong> {info.SEAL_TYPE}
                            <br />
                        </p>
                        <p className='mb-1'>
                            <strong>Design Pressure (Bar):</strong> {info.DESIGN_PRESSURE_BAR || 'N/A'}
                            <br />
                        </p>
                        <p className='mb-1'>
                            <strong>Direction of Rotation:</strong> {info.DIRECTION_OF_ROTATION}
                            <br />
                        </p>
                        <p className='mb-1'>
                            <strong>Trip Speed (RPM):</strong> {info.TRIP_SPEED_RPM || 'N/A'}
                            <br />
                        </p>
                        <p className='mb-1'>
                            <strong>Maximum Operating Speed (RPM):</strong> {info.MAXIMUM_OPERATING_SPEED_RPM}
                        </p>
                    </li>
                ))}
            </ul>
        </>
    );
};

const getAlarmLimitValue = (arr, index) => {
    if (!Array.isArray(arr)) {
        return 'N/A';
    }
    if (arr[index] === undefined || arr[index] === null) {
        return 'N/A';
    }
    return arr[index];
};

const getAlarmLimitsContent = data => {
    const depTags = parseCsvLikeField(data.DEP_TAG_ALIAS)
        .split(', ')
        .filter(Boolean);
    if (depTags.length === 0) {
        return '-';
    }

    const lines = depTags.map((tag, index) => {
        const highValue = getAlarmLimitValue(data.HIGH_LIMIT_ARRAY, index);
        const lowValue = getAlarmLimitValue(data.LOW_LIMIT_ARRAY, index);
        return `${tag} - High Alarm : ${highValue}, Low Alarm : ${lowValue}`;
    });

    return lines.join('\n');
};

const renderTrainingStatusCell = row => {
    if (isTrainingSucceededStatus(row.TRAINING_STATUS)) {
        return (
            <div className='text-green-500 font-medium flex items-center gap-1' title='Completed'>
                <FaCheck />
                Completed
            </div>
        );
    }

    if (isTrainingInProgressStatus(row.TRAINING_STATUS)) {
        return (
            <div className='text-yellow-600 font-medium flex items-center gap-1' title='In Progress'>
                <RiProgress5Line />
                In Progress
            </div>
        );
    }

    return (
        <div className='text-red-600 flex font-medium items-center gap-1.5' title='Failed'>
            <ImCancelCircle /> Failed
        </div>
    );
};

const ExpandedComponent = ({ data }) => {
    const dgsTags = parseCsvLikeField(data.TAG_ALIAS_LIST, '; ');
    let tagDescription = '-';
    if (data.TAG_NAME) {
        tagDescription = parseCsvLikeField(data.TAG_NAME);
    }

    const forecastJobName = getForecastJobName(data) || '-';
    const frequencyText = data.DATA_FREQ ?? '-';
    let frequencySuffix = '';
    if (data.DATA_FREQ) {
        frequencySuffix = ' mins';
    }

    const projectInfo = getProjectInfo(data);
    const businessRulesTable = getBusinessRulesTable(data);

    return (
        <div
            style={{
                padding: '10px 20px',
                fontSize: '13px',
                backgroundColor: '#f9f9f9',
                display: 'flex',
                gap: '10px'
            }}
        >
            <div className='w-[450px]'>
                <p className='mb-1'>
                    <strong>Custom Name : </strong> {data.CUSTOM_NAME}
                </p>
                <p className='mb-1'>
                    <strong>Custom Description : </strong> {data.CUSTOM_DESC ?? '-'}
                </p>
                <p className='mb-1'>
                    <strong>DGS Tags : </strong> {dgsTags}
                </p>
                <p className='mb-1'>
                    <strong>Tag Description : </strong> {tagDescription}
                </p>
                <p className='mb-1'>
                    <strong>Alarm Limits : </strong>
                    <br />
                    <pre style={{ margin: 0, padding: 0, background: 'none' }}>
                        {getAlarmLimitsContent(data)}
                    </pre>
                </p>
                <p className='mb-1'>
                    <strong>Forecast Job Name : </strong> {forecastJobName}
                </p>
                <p className='mb-1'>
                    <strong>Datasource Id : </strong> {data.DATASOURCE}
                </p>
                <p className='mb-1'>
                    <strong>Project Name : </strong> {data.PROJECTNAME}
                </p>
                <p className='mb-1'>
                    <strong>Driver : </strong>
                    {data.DRIVER}
                </p>
                <p className='mb-1'>
                    <strong>Future Start Date : </strong>
                    {data.FUT_ST_DATE ?? '-'}
                </p>
                <p className='mb-1'>
                    <strong>Future End Date : </strong>
                    {data.FUT_END_DATE ?? '-'}
                </p>
                <p className='mb-1'>
                    <strong>Frequency : </strong>
                    {frequencyText}
                    {frequencySuffix}
                </p>
            </div>

            <div>
                <ProjectInfoSection projectInfo={projectInfo} />

                <div className='mt-3'>
                    <strong>Business Rule : </strong>
                    {businessRulesTable}
                </div>
            </div>
        </div>
    );
};

const LiveForecastsTable = ({ modelsData, onViewForecast, updateModelStatus, loading, setLoading, deleteModel, reloadModels }) => {
    const [modelToDelete, setModelToDelete] = useState(null);
    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [expandedRow, setExpandedRow] = useState(null);

    const handleDeleteClick = index => {
        setModelToDelete(index);
        setIsPopupVisible(true);
    };

    const closeDeletePopup = () => {
        setIsPopupVisible(false);
        setModelToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (modelToDelete === null) {
            return;
        }

        const model = modelsData[modelToDelete];
        const forecastJobName = getForecastJobName(model);
        if (!forecastJobName) {
            toast.error('Forecast job name is missing for this model.');
            closeDeletePopup();
            return;
        }

        setLoading(true);
        try {
            const response = await deleteModel(forecastJobName);
            if (response.message === 'Forecast job status updated to DELETED') {
                toast.success('Model deleted successfully!');
                if (reloadModels) {
                    reloadModels();
                }
            } else {
                toast.error('Model cannot be deleted');
            }
        } catch (error) {
            toast.error('Error while deleting model. Please try again!');
            console.error('Error deleting model:', error);
        } finally {
            setLoading(false);
            closeDeletePopup();
        }
    };

    const handleRowClick = row => {
        if (expandedRow === row) {
            setExpandedRow(null);
            return;
        }
        setExpandedRow(row);
    };

    const handleToggle = async index => {
        const model = modelsData[index];
        const forecastJobName = getForecastJobName(model);
        if (!forecastJobName) {
            toast.error('Forecast job name is missing for this model.');
            return;
        }

        let newStatus = 'ACTIVE';
        if (model.STATUS === 'ACTIVE') {
            newStatus = 'INACTIVE';
        }

        setLoading(true);
        try {
            await updateModelStatus(forecastJobName, newStatus);
            toast.success('Active status updated successfully!');
            if (reloadModels) {
                reloadModels();
            }
        } catch (error) {
            toast.error('Error while updating active status. Please try again!');
            console.error('Error updating model active status:', error);
        } finally {
            setLoading(false);
        }
    };

    const conditionalRowStyles = [
        {
            when: row => row === expandedRow,
            style: {
                borderBottom: 'none',
                backgroundColor: '#ededed'
            }
        }
    ];

    const getOverflowStyle = maxWidth => ({
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth
    });

    const columns = [
        {
            name: 'Name',
            selector: row => row.CUSTOM_NAME,
            sortable: true,
            grow: 1.5,
            cell: row => (
                <div title={row.CUSTOM_NAME} style={getOverflowStyle('110px')}>
                    {row.CUSTOM_NAME}
                </div>
            )
        },
        {
            name: 'DGS Tags',
            selector: row => parseCsvLikeField(row.TAG_ALIAS_LIST, '; '),
            sortable: true,
            grow: 1,
            cell: row => {
                const tags = parseCsvLikeField(row.TAG_ALIAS_LIST, '; ');
                return (
                    <div title={tags} style={getOverflowStyle('210px')}>
                        {tags}
                    </div>
                );
            }
        },
        {
            name: 'Created Date',
            selector: row => row.CREATED_AT,
            sortable: true,
            width: '130px',
            cell: row => (
                <div title={row.CREATED_AT} style={getOverflowStyle('120px')}>
                    {row.CREATED_AT}
                </div>
            )
        },
        {
            name: 'Start Date',
            selector: row => row.TRAINING_ST_DATE,
            sortable: true,
            width: '120px',
            cell: row => (
                <div title={row.TRAINING_ST_DATE} style={getOverflowStyle('110px')}>
                    {row.TRAINING_ST_DATE}
                </div>
            )
        },
        {
            name: 'End Date',
            selector: row => row.TRAINING_END_DATE,
            sortable: true,
            cell: row => (
                <div title={row.TRAINING_END_DATE} style={getOverflowStyle('110px')}>
                    {row.TRAINING_END_DATE}
                </div>
            )
        },
        {
            name: 'Training Status',
            cell: row => renderTrainingStatusCell(row),
            sortable: true,
            width: '135px'
        },
        {
            name: 'Active',
            cell: (row, index) => (
                <label className='flex items-center cursor-pointer'>
                    <input
                        type='checkbox'
                        className='sr-only peer'
                        checked={row.STATUS === 'ACTIVE'}
                        onChange={() => handleToggle(index)}
                    />
                    <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-300 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                </label>
            ),
            width: '69px'
        },
        {
            name: 'Actions',
            cell: (row, index) => {
                let deleteClassName = 'cursor-pointer text-red-600';
                let isActiveModel = false;
                if (row.STATUS === 'ACTIVE') {
                    deleteClassName = `${deleteClassName} opacity-50 cursor-not-allowed`;
                    isActiveModel = true;
                }

                const onDeleteClick = () => {
                    if (!isActiveModel) {
                        handleDeleteClick(index);
                    }
                };

                return (
                    <div className='cursor-pointer text-btn-primary flex gap-4'>
                        <MdDelete onClick={onDeleteClick} className={deleteClassName} size={20} />
                    </div>
                );
            },
            width: '87px'
        },
        {
            name: 'View Forecast',
            cell: row => {
                const onViewClick = () => {
                    if (onViewForecast) {
                        onViewForecast(row);
                    }
                };

                return (
                    <button
                        className='flex items-center gap-2 px-4 py-2 bg-btn-primary text-white rounded hover:bg-blue-700 transition-colors'
                        onClick={onViewClick}
                        title='View Forecast Details'
                    >
                        <FaEye size={16} />
                        View
                    </button>
                );
            },
            sortable: true,
            width: '150px'
        }
    ];

    const customStyles = {
        headCells: {
            style: {
                fontSize: '13px',
                backgroundColor: '#e3e1e1'
            }
        },
        cells: {
            style: {
                fontSize: '13.4px'
            }
        },
        table: {
            style: {
                border: '1px solid #e3e1e1',
                margin: '0 auto'
            }
        }
    };

    let tableContent = null;
    if (!loading) {
        tableContent = (
            <div className='w-[65%]'>
                <p className='font-semibold text-2xl text-btn-primary w-[65%] mb-5'>
                    Existing Models
                </p>
                <DataTable
                    columns={columns}
                    data={modelsData}
                    responsive
                    customStyles={customStyles}
                    expandableRows
                    expandOnRowClicked
                    expandableRowsHideExpander
                    expandableRowExpanded={row => row === expandedRow}
                    onRowClicked={handleRowClick}
                    expandableRowsComponent={ExpandedComponent}
                    conditionalRowStyles={conditionalRowStyles}
                />
            </div>
        );
    }

    let loadingContent = null;
    if (loading) {
        loadingContent = (
            <div className='flex flex-col items-center justify-center w-full h-32'>
                <span className='mt-2 text-gray-700'>Loading table...</span>
            </div>
        );
    }

    return (
        <>
            {loadingContent}
            {tableContent}
            {isPopupVisible && (
                <ConfirmationPopup
                    message='Are you sure you want to delete this model?'
                    onConfirm={handleConfirmDelete}
                    onCancel={closeDeletePopup}
                    cancelButtonMsg='No, cancel'
                    confirmButtonMsg="Yes, I'm sure"
                />
            )}
        </>
    );
};

export default LiveForecastsTable;
